import { Language } from './translations';

export interface PrivacyContent {
  pageTitle: string;
  intro: string;
  s1Title: string; s1Intro: string; s1Items: string[];
  s2Title: string; s2Intro: string; s2Items: string[];
  s3Title: string; s3Text: string;
  s4Title: string; s4Text: string;
  s5Title: string; s5Text: string;
  s6Title: string; s6Intro: string; s6Items: string[];
  s7Title: string; s7Text: string;
  s8Title: string; s8Text: string;
  contactTitle: string; contactText: string;
}

export interface TermsContent {
  pageTitle: string;
  intro: string;
  s1Title: string; s1Text: string;
  s2Title: string; s2Intro: string; s2Items: string[];
  warningTitle: string; warningText: string;
  s3Title: string; s3Intro: string; s3Items: string[];
  s4Title: string; s4Intro: string; s4Items: string[];
  s5Title: string; s5Intro: string; s5Items: string[]; s5Note: string;
  s6Title: string; s6Intro: string; s6Items: string[]; s6Note: string;
  s7Title: string; s7Intro: string; s7Items: string[];
  s8Title: string; s8Text: string;
  s9Title: string; s9Intro: string; s9Items: string[];
  s10Title: string; s10Text: string;
  s11Title: string; s11Text: string;
  s12Title: string; s12Intro: string; s12Items: string[]; s12Note: string;
  contactTitle: string; contactText: string; contactText2: string;
}

export interface LegalTranslations {
  backButton: string;
  lastUpdated: string;
  privacy: PrivacyContent;
  terms: TermsContent;
}

export const legalTranslations: Record<Language, LegalTranslations> = {
  // ─────────────────────────────── ENGLISH ───────────────────────────────
  en: {
    backButton: '← Back to Cook AI',
    lastUpdated: 'Last Updated: January 2025',
    privacy: {
      pageTitle: 'Privacy Policy',
      intro: 'At Cook AI, we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your data when you use our AI-powered recipe generation application.',
      s1Title: '1. Information We Collect',
      s1Intro: 'When you use Cook AI, we may collect the following types of information:',
      s1Items: [
        'Ingredient Data: Information about ingredients you input to generate recipes',
        'Recipe Preferences: Your dietary preferences, cuisine types, and cooking methods',
        'Usage Data: How you interact with our app, including features used and time spent',
        'Device Information: Browser type, device type, and operating system for app optimization',
      ],
      s2Title: '2. How We Use Your Information',
      s2Intro: 'We use the information we collect to:',
      s2Items: [
        'Generate personalized recipe recommendations based on your ingredients',
        'Improve our AI algorithms and recipe generation quality',
        'Enhance user experience and app functionality',
        'Analyze usage patterns to develop new features',
        'Ensure the security and proper functioning of our service',
      ],
      s3Title: '3. Data Storage and Security',
      s3Text: 'Your data is stored securely and we implement industry-standard security measures to protect it. We do not sell, trade, or rent your personal information to third parties. Your ingredient lists and generated recipes are stored temporarily to provide you with the best experience.',
      s4Title: '4. AI and Third-Party Services',
      s4Text: 'Cook AI uses artificial intelligence technology to generate recipes. The ingredient data you provide may be processed by our AI service providers to create recipe suggestions. These providers are contractually obligated to protect your data and use it only for recipe generation purposes.',
      s5Title: '5. Cookies and Local Storage',
      s5Text: 'We use browser storage technologies to save your preferences and improve your experience. This includes remembering your theme preference, recent ingredients, and favorite recipes. You can clear this data at any time through your browser settings.',
      s6Title: '6. Your Rights',
      s6Intro: 'You have the right to:',
      s6Items: [
        'Access the personal data we hold about you',
        'Request correction of inaccurate data',
        'Request deletion of your data',
        'Opt-out of data collection (though this may limit app functionality)',
        'Export your data in a portable format',
      ],
      s7Title: "7. Children's Privacy",
      s7Text: 'Cook AI is designed for general audiences. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such information, please contact us immediately.',
      s8Title: '8. Changes to This Policy',
      s8Text: 'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.',
      contactTitle: 'Contact Us',
      contactText: 'If you have any questions about this Privacy Policy or our data practices, please contact us at:',
    },
    terms: {
      pageTitle: 'Terms of Use',
      intro: 'Welcome to Cook AI! By accessing or using our AI-powered recipe generation application, you agree to be bound by these Terms of Use. Please read them carefully before using our service.',
      s1Title: '1. Acceptance of Terms',
      s1Text: 'By using Cook AI, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use and our Privacy Policy. If you do not agree with any part of these terms, you should not use our service.',
      s2Title: '2. Description of Service',
      s2Intro: 'Cook AI is an AI-powered application that generates personalized recipes based on ingredients you provide. Our service includes:',
      s2Items: [
        'AI-generated recipe suggestions based on your input ingredients',
        'Cooking instructions and preparation steps',
        'Nutritional information estimates',
        'Recipe customization options',
        'Ingredient substitution recommendations',
      ],
      warningTitle: '⚠️ Important Disclaimer',
      warningText: 'Cook AI provides recipe suggestions generated by artificial intelligence. While we strive for accuracy, we cannot guarantee that all recipes are safe, accurate, or suitable for your dietary needs. Always use your judgment and consult healthcare professionals for dietary advice.',
      s3Title: '3. User Responsibilities',
      s3Intro: 'When using Cook AI, you agree to:',
      s3Items: [
        'Provide accurate ingredient information',
        'Use the service for lawful purposes only',
        'Not attempt to manipulate or abuse the AI system',
        'Verify recipe safety and suitability before cooking',
        'Check for food allergies and dietary restrictions',
        'Use proper food safety practices when cooking',
      ],
      s4Title: '4. AI-Generated Content',
      s4Intro: 'All recipes and cooking instructions are generated by artificial intelligence technology. While our AI is trained on extensive culinary data, please note:',
      s4Items: [
        'AI-generated recipes may occasionally contain errors or unusual combinations',
        'Cooking times and temperatures should be verified for safety',
        'Nutritional information is estimated and may not be completely accurate',
        'Always check ingredient compatibility and food safety guidelines',
      ],
      s5Title: '5. Food Safety and Allergies',
      s5Intro: 'You are solely responsible for:',
      s5Items: [
        'Checking all ingredients for allergens before cooking',
        'Ensuring food is cooked to safe temperatures',
        'Following proper food handling and storage procedures',
        'Consulting medical professionals about dietary restrictions',
        'Verifying ingredient freshness and quality',
      ],
      s5Note: 'Cook AI is not responsible for any allergic reactions, foodborne illness, or health issues resulting from recipes generated by our service.',
      s6Title: '6. Intellectual Property',
      s6Intro: 'The Cook AI application, including its design, features, and underlying technology, is owned by Cook AI and protected by intellectual property laws. AI-generated recipes are provided for your personal use. You may:',
      s6Items: [
        'Use recipes for personal, non-commercial cooking',
        'Share recipes with friends and family',
        'Modify recipes to suit your preferences',
      ],
      s6Note: 'You may not republish, redistribute, or commercially exploit recipes generated by Cook AI without permission.',
      s7Title: '7. Limitation of Liability',
      s7Intro: 'Cook AI provides the service "as is" without warranties of any kind. We are not liable for:',
      s7Items: [
        'Accuracy or reliability of AI-generated recipes',
        'Food safety issues or allergic reactions',
        'Cooking failures or unsatisfactory results',
        'Damages resulting from use of our service',
        'Service interruptions or technical issues',
      ],
      s8Title: '8. Service Modifications',
      s8Text: 'We reserve the right to modify, suspend, or discontinue Cook AI at any time without notice. We may also update these Terms of Use periodically. Continued use of the service after changes constitutes acceptance of the updated terms.',
      s9Title: '9. Prohibited Uses',
      s9Intro: 'You may not use Cook AI to:',
      s9Items: [
        'Generate recipes for harmful or dangerous purposes',
        'Attempt to reverse engineer or manipulate our AI system',
        'Scrape or extract data from our service',
        'Violate any applicable laws or regulations',
        "Interfere with other users' access to the service",
      ],
      s10Title: '10. Termination',
      s10Text: 'We reserve the right to terminate or suspend your access to Cook AI at any time, without notice, for any reason, including violation of these Terms of Use.',
      s11Title: '11. Governing Law',
      s11Text: 'These Terms of Use are governed by and construed in accordance with applicable laws. Any disputes arising from these terms or use of Cook AI shall be resolved through appropriate legal channels.',
      s12Title: '12. Subscriptions & Billing',
      s12Intro: 'Cook AI offers auto-renewable subscription plans ("Cook AI Pro") that unlock premium features. By subscribing, you agree to the following terms:',
      s12Items: [
        'Subscription Plans: Cook AI Pro is available as a Weekly plan (with a 7-day free trial) or a Yearly plan. The Yearly plan offers the best value.',
        'Free Trial: The Weekly plan includes a 7-day free trial. Your subscription begins automatically at the end of the trial period unless cancelled at least 24 hours before the trial ends.',
        'Pricing: Subscription prices are displayed in the App Store at the time of purchase and may vary by country or region.',
        'Payment: Payment is charged to your Apple ID account upon confirmation of purchase or at the end of the free trial period.',
        'Automatic Renewal: Subscriptions automatically renew at the end of each billing period unless cancelled at least 24 hours before renewal. Your Apple ID account is charged within 24 hours prior to the end of the current period.',
        'Cancellation: Cancel anytime through device Settings → [Your Name] → Subscriptions. Access continues until the end of the current billing period.',
        'No Refunds: Subscription fees are non-refundable except as required by applicable law or Apple\'s refund policies.',
        'Premium Features: Cook AI Pro unlocks unlimited AI-powered recipes, saving and organizing recipes, advanced nutritional insights, and an ad-free experience.',
      ],
      s12Note: 'Subscription management is handled through your Apple ID account settings. For billing questions or refund requests, contact Apple Support.',
      contactTitle: 'Contact Us',
      contactText: 'If you have any questions about these Terms of Use, please contact us at:',
      contactText2: 'By using Cook AI, you acknowledge that you have read and understood these Terms of Use and agree to be bound by them. Happy cooking! 👨‍🍳',
    },
  },

  // ─────────────────────────────── ARABIC ────────────────────────────────
  ar: {
    backButton: '→ العودة إلى Cook AI',
    lastUpdated: 'آخر تحديث: يناير 2025',
    privacy: {
      pageTitle: 'سياسة الخصوصية',
      intro: 'في Cook AI، نحترم خصوصيتك ونلتزم بحماية معلوماتك الشخصية. توضح سياسة الخصوصية هذه كيفية جمع بياناتك واستخدامها وحمايتها عند استخدام تطبيقنا لتوليد الوصفات بالذكاء الاصطناعي.',
      s1Title: '1. المعلومات التي نجمعها',
      s1Intro: 'عند استخدام Cook AI، قد نجمع أنواعاً من المعلومات التالية:',
      s1Items: [
        'بيانات المكونات: معلومات حول المكونات التي تدخلها لتوليد الوصفات',
        'تفضيلات الوصفات: تفضيلاتك الغذائية وأنواع المطبخ وطرق الطهي',
        'بيانات الاستخدام: كيفية تفاعلك مع التطبيق، بما في ذلك الميزات المستخدمة والوقت المستغرق',
        'معلومات الجهاز: نوع المتصفح والجهاز ونظام التشغيل لتحسين التطبيق',
      ],
      s2Title: '2. كيف نستخدم معلوماتك',
      s2Intro: 'نستخدم المعلومات التي نجمعها من أجل:',
      s2Items: [
        'توليد توصيات وصفات مخصصة بناءً على مكوناتك',
        'تحسين خوارزميات الذكاء الاصطناعي وجودة توليد الوصفات',
        'تعزيز تجربة المستخدم ووظائف التطبيق',
        'تحليل أنماط الاستخدام لتطوير ميزات جديدة',
        'ضمان أمن وحسن سير الخدمة',
      ],
      s3Title: '3. تخزين البيانات وأمنها',
      s3Text: 'يتم تخزين بياناتك بأمان ونطبق تدابير أمنية بمستوى المعيار الصناعي لحمايتها. لا نبيع أو نتاجر أو نؤجر معلوماتك الشخصية لأطراف ثالثة. يتم تخزين قوائم المكونات والوصفات المولّدة مؤقتاً لتزويدك بأفضل تجربة.',
      s4Title: '4. الذكاء الاصطناعي وخدمات الطرف الثالث',
      s4Text: 'يستخدم Cook AI تقنية الذكاء الاصطناعي لتوليد الوصفات. قد تتم معالجة بيانات المكونات التي تقدمها بواسطة مزودي خدمات الذكاء الاصطناعي لإنشاء اقتراحات الوصفات. يلتزم هؤلاء المزودون تعاقدياً بحماية بياناتك واستخدامها فقط لأغراض توليد الوصفات.',
      s5Title: '5. ملفات تعريف الارتباط والتخزين المحلي',
      s5Text: 'نستخدم تقنيات تخزين المتصفح لحفظ تفضيلاتك وتحسين تجربتك. يشمل ذلك تذكر تفضيل السمة والمكونات الأخيرة والوصفات المفضلة. يمكنك مسح هذه البيانات في أي وقت من خلال إعدادات المتصفح.',
      s6Title: '6. حقوقك',
      s6Intro: 'لديك الحق في:',
      s6Items: [
        'الوصول إلى البيانات الشخصية التي نحتفظ بها عنك',
        'طلب تصحيح البيانات غير الدقيقة',
        'طلب حذف بياناتك',
        'الانسحاب من جمع البيانات (وإن كان ذلك قد يحد من وظائف التطبيق)',
        'تصدير بياناتك بصيغة محمولة',
      ],
      s7Title: '7. خصوصية الأطفال',
      s7Text: 'يستهدف Cook AI الجماهير العامة. لا نجمع عن قصد معلومات شخصية من الأطفال دون 13 عاماً. إذا كنت تعتقد أننا جمعنا مثل هذه المعلومات عن غير قصد، يرجى التواصل معنا فوراً.',
      s8Title: '8. التغييرات على هذه السياسة',
      s8Text: 'قد نحدّث سياسة الخصوصية هذه من وقت لآخر. سنخطرك بأي تغييرات عن طريق نشر سياسة الخصوصية الجديدة على هذه الصفحة وتحديث تاريخ "آخر تحديث".',
      contactTitle: 'تواصل معنا',
      contactText: 'إذا كان لديك أي أسئلة حول سياسة الخصوصية هذه أو ممارسات البيانات لدينا، يرجى التواصل معنا على:',
    },
    terms: {
      pageTitle: 'شروط الاستخدام',
      intro: 'مرحباً بك في Cook AI! من خلال الوصول إلى تطبيقنا أو استخدامه، فإنك توافق على الالتزام بشروط الاستخدام هذه. يرجى قراءتها بعناية قبل استخدام خدمتنا.',
      s1Title: '1. قبول الشروط',
      s1Text: 'باستخدام Cook AI، فإنك تقر بأنك قرأت هذه الشروط وفهمتها وتوافق على الالتزام بها وبسياسة الخصوصية. إذا كنت لا توافق على أي جزء من هذه الشروط، فلا ينبغي لك استخدام خدمتنا.',
      s2Title: '2. وصف الخدمة',
      s2Intro: 'Cook AI هو تطبيق مدعوم بالذكاء الاصطناعي يولّد وصفات مخصصة بناءً على المكونات التي تقدمها. تشمل خدمتنا:',
      s2Items: [
        'اقتراحات وصفات مولّدة بالذكاء الاصطناعي بناءً على مكوناتك',
        'تعليمات الطهي وخطوات التحضير',
        'تقديرات المعلومات الغذائية',
        'خيارات تخصيص الوصفات',
        'توصيات بديل المكونات',
      ],
      warningTitle: '⚠️ إخلاء مسؤولية هام',
      warningText: 'يقدم Cook AI اقتراحات وصفات مولّدة بالذكاء الاصطناعي. بينما نسعى للدقة، لا يمكننا ضمان أن جميع الوصفات آمنة أو دقيقة أو مناسبة لاحتياجاتك الغذائية. استخدم دائماً حكمك واستشر متخصصي الرعاية الصحية للحصول على نصائح غذائية.',
      s3Title: '3. مسؤوليات المستخدم',
      s3Intro: 'عند استخدام Cook AI، فإنك توافق على:',
      s3Items: [
        'تقديم معلومات دقيقة عن المكونات',
        'استخدام الخدمة للأغراض المشروعة فقط',
        'عدم محاولة التلاعب أو إساءة استخدام نظام الذكاء الاصطناعي',
        'التحقق من سلامة الوصفة وملاءمتها قبل الطهي',
        'التحقق من الحساسيات الغذائية والقيود',
        'اتباع ممارسات سلامة الغذاء الصحيحة',
      ],
      s4Title: '4. المحتوى المولّد بالذكاء الاصطناعي',
      s4Intro: 'يتم توليد جميع الوصفات وتعليمات الطهي بواسطة تقنية الذكاء الاصطناعي. يرجى ملاحظة:',
      s4Items: [
        'قد تحتوي الوصفات المولّدة أحياناً على أخطاء أو تركيبات غير معتادة',
        'يجب التحقق من أوقات وحرارة الطهي لضمان السلامة',
        'المعلومات الغذائية تقديرية وقد لا تكون دقيقة تماماً',
        'تحقق دائماً من توافق المكونات وإرشادات سلامة الغذاء',
      ],
      s5Title: '5. سلامة الغذاء والحساسيات',
      s5Intro: 'أنت مسؤول وحدك عن:',
      s5Items: [
        'التحقق من وجود مسببات الحساسية في جميع المكونات قبل الطهي',
        'التأكد من طهي الطعام على درجات حرارة آمنة',
        'اتباع إجراءات التعامل مع الطعام وتخزينه بشكل صحيح',
        'استشارة متخصصين طبيين بشأن القيود الغذائية',
        'التحقق من نضارة المكونات وجودتها',
      ],
      s5Note: 'Cook AI غير مسؤول عن أي ردود فعل تحسسية أو أمراض منقولة بالغذاء أو مشاكل صحية ناتجة عن الوصفات المولّدة بخدمتنا.',
      s6Title: '6. الملكية الفكرية',
      s6Intro: 'تطبيق Cook AI، بما في ذلك تصميمه وميزاته والتقنية الأساسية، مملوك لـ Cook AI ومحمي بقوانين الملكية الفكرية. الوصفات المولّدة بالذكاء الاصطناعي مقدمة للاستخدام الشخصي. يمكنك:',
      s6Items: [
        'استخدام الوصفات للطهي الشخصي غير التجاري',
        'مشاركة الوصفات مع الأصدقاء والعائلة',
        'تعديل الوصفات لتناسب تفضيلاتك',
      ],
      s6Note: 'لا يجوز لك إعادة نشر الوصفات أو توزيعها أو استغلالها تجارياً دون إذن.',
      s7Title: '7. تحديد المسؤولية',
      s7Intro: 'تقدم Cook AI الخدمة "كما هي" دون أي ضمانات. لسنا مسؤولين عن:',
      s7Items: [
        'دقة أو موثوقية الوصفات المولّدة بالذكاء الاصطناعي',
        'مشكلات سلامة الغذاء أو ردود الفعل التحسسية',
        'فشل الطهي أو النتائج غير المرضية',
        'الأضرار الناتجة عن استخدام خدمتنا',
        'انقطاع الخدمة أو المشكلات الفنية',
      ],
      s8Title: '8. تعديلات الخدمة',
      s8Text: 'نحتفظ بالحق في تعديل Cook AI أو تعليقه أو إيقافه في أي وقت دون إشعار. قد نحدّث أيضاً شروط الاستخدام هذه بشكل دوري.',
      s9Title: '9. الاستخدامات المحظورة',
      s9Intro: 'لا يجوز لك استخدام Cook AI لـ:',
      s9Items: [
        'توليد وصفات لأغراض ضارة أو خطيرة',
        'محاولة الهندسة العكسية أو التلاعب بنظام الذكاء الاصطناعي',
        'استخراج البيانات من خدمتنا',
        'انتهاك أي قوانين أو لوائح سارية',
        'التدخل في وصول المستخدمين الآخرين للخدمة',
      ],
      s10Title: '10. الإنهاء',
      s10Text: 'نحتفظ بالحق في إنهاء أو تعليق وصولك إلى Cook AI في أي وقت، دون إشعار، لأي سبب.',
      s11Title: '11. القانون الحاكم',
      s11Text: 'تحكم هذه الشروط القوانين المعمول بها ويُفسَّر بموجبها. يتم حل أي نزاعات تنشأ عن هذه الشروط أو استخدام Cook AI عبر القنوات القانونية المناسبة.',
      s12Title: '12. الاشتراكات والفوترة',
      s12Intro: 'يقدم Cook AI خطط اشتراك قابلة للتجديد التلقائي ("Cook AI Pro") تتيح الوصول إلى الميزات المميزة. بالاشتراك، توافق على الشروط التالية:',
      s12Items: [
        'خطط الاشتراك: يتوفر Cook AI Pro كخطة أسبوعية (مع تجربة مجانية لمدة 7 أيام) أو خطة سنوية توفر أفضل قيمة.',
        'الفترة التجريبية: تتضمن الخطة الأسبوعية تجربة مجانية لمدة 7 أيام. يبدأ الاشتراك تلقائياً عند انتهاء الفترة التجريبية ما لم تلغِه قبل 24 ساعة على الأقل من انتهائها.',
        'الأسعار: تُعرض أسعار الاشتراك في متجر التطبيقات وقت الشراء وقد تختلف حسب البلد أو المنطقة.',
        'الدفع: يُحسم المبلغ من حساب Apple ID الخاص بك عند تأكيد الشراء أو عند انتهاء الفترة التجريبية.',
        'التجديد التلقائي: تتجدد الاشتراكات تلقائياً في نهاية كل فترة إلا إذا تم الإلغاء قبل 24 ساعة على الأقل من تاريخ التجديد. يُحسم مبلغ حساب Apple ID خلال 24 ساعة قبل نهاية الفترة الحالية.',
        'الإلغاء: يمكنك الإلغاء في أي وقت من خلال الإعدادات ← [اسمك] ← الاشتراكات. يستمر الوصول حتى نهاية فترة الفوترة الحالية.',
        'عدم الاسترداد: رسوم الاشتراك غير قابلة للاسترداد إلا وفقاً للقانون المعمول به أو سياسات استرداد Apple.',
        'الميزات المميزة: يتيح Cook AI Pro وصفات غير محدودة بالذكاء الاصطناعي، وحفظ الوصفات وتنظيمها، ورؤى غذائية متقدمة، وتجربة خالية من الإعلانات.',
      ],
      s12Note: 'تُدار إدارة الاشتراك من خلال إعدادات حساب Apple ID الخاص بك. للاستفسارات المتعلقة بالفوترة أو طلبات الاسترداد، يرجى التواصل مع دعم Apple.',
      contactTitle: 'تواصل معنا',
      contactText: 'إذا كان لديك أي أسئلة حول شروط الاستخدام هذه، يرجى التواصل معنا على:',
      contactText2: 'باستخدام Cook AI، فإنك تقر بأنك قرأت شروط الاستخدام هذه وفهمتها وتوافق على الالتزام بها. طهياً سعيداً! 👨‍🍳',
    },
  },

  // ──────────────────────────── AZERBAIJANI ──────────────────────────────
  az: {
    backButton: '← Cook AI-yə Qayıt',
    lastUpdated: 'Son Yenilənmə: Yanvar 2025',
    privacy: {
      pageTitle: 'Məxfilik Siyasəti',
      intro: 'Cook AI olaraq, məxfiliyinizə hörmət edirik və şəxsi məlumatlarınızı qorumağa sadiqik. Bu Məxfilik Siyasəti, AI dəstəkli resept yaratma tətbiqimizdən istifadə edərkən məlumatlarınızı necə topladığımızı, istifadə etdiyimizi və qoruduğumuzu izah edir.',
      s1Title: '1. Topladığımız Məlumatlar',
      s1Intro: 'Cook AI istifadə edərkən aşağıdakı məlumatları toplaya bilərik:',
      s1Items: [
        'Maddə Məlumatları: Resept yaratmaq üçün daxil etdiyiniz maddələr haqqında məlumat',
        'Resept Tərcihlər: Pəhriz tərcihləriniiz, mətbəx növləri və bişirmə üsulları',
        'İstifadə Məlumatları: Tətbiqlə necə qarşılıqlı əlaqə qurduğunuz, o cümlədən istifadə edilən xüsusiyyətlər',
        'Cihaz Məlumatları: Tətbiqin optimallaşdırılması üçün brauzer növü, cihaz növü və əməliyyat sistemi',
      ],
      s2Title: '2. Məlumatlarınızı Necə İstifadə Edirik',
      s2Intro: 'Topladığımız məlumatları aşağıdakı məqsədlər üçün istifadə edirik:',
      s2Items: [
        'Maddələrinizə əsasən fərdi resept tövsiyələri yaratmaq',
        'Süni intellekt alqoritmlərimizi və resept yaratma keyfiyyətini yaxşılaşdırmaq',
        'İstifadəçi təcrübəsini və tətbiq funksionallığını artırmaq',
        'Yeni xüsusiyyətlər inkişaf etdirmək üçün istifadə nümunələrini təhlil etmək',
        'Xidmətin təhlükəsizliyini və düzgün işləməsini təmin etmək',
      ],
      s3Title: '3. Məlumatların Saxlanması və Təhlükəsizliyi',
      s3Text: 'Məlumatlarınız təhlükəsiz şəkildə saxlanır və onları qorumaq üçün sənaye standartı təhlükəsizlik tədbirləri tətbiq edirik. Şəxsi məlumatlarınızı üçüncü tərəflərə satmırıq, ticarət etmirik və ya icarəyə vermirik.',
      s4Title: '4. Süni İntellekt və Üçüncü Tərəf Xidmətlər',
      s4Text: 'Cook AI resept yaratmaq üçün süni intellekt texnologiyasından istifadə edir. Maddə məlumatlarınız resept təkliflərini yaratmaq üçün xidmət təminatçılarımız tərəfindən işlənə bilər.',
      s5Title: '5. Çərəzlər və Yerli Saxlama',
      s5Text: 'Tərcihləriniizi saxlamaq və təcrübənizi yaxşılaşdırmaq üçün brauzer saxlama texnologiyalarından istifadə edirik. Brauzer parametrləri vasitəsilə bu məlumatları istənilən vaxt silə bilərsiniz.',
      s6Title: '6. Hüquqlarınız',
      s6Intro: 'Aşağıdakılara hüququnuz var:',
      s6Items: [
        'Haqqınızda saxladığımız şəxsi məlumatlara giriş',
        'Yanlış məlumatların düzəldilməsini tələb etmək',
        'Məlumatlarınızın silinməsini tələb etmək',
        'Məlumat toplanmasından imtina etmək (bu tətbiqin funksionallığını məhdudlaşdıra bilər)',
        'Məlumatlarınızı daşınan formatda ixrac etmək',
      ],
      s7Title: '7. Uşaqların Məxfiliyi',
      s7Text: 'Cook AI geniş auditoriyalar üçün nəzərdə tutulmuşdur. 13 yaşdan kiçik uşaqlardan şüurlu şəkildə şəxsi məlumat toplamırıq.',
      s8Title: '8. Bu Siyasətdəki Dəyişikliklər',
      s8Text: 'Bu Məxfilik Siyasətini vaxtaşırı yeniləyə bilərik. Yeni siyasəti bu səhifədə dərc etməklə dəyişikliklərdən sizi xəbərdar edəcəyik.',
      contactTitle: 'Bizimlə Əlaqə',
      contactText: 'Bu Məxfilik Siyasəti haqqında suallarınız varsa, bizimlə əlaqə saxlayın:',
    },
    terms: {
      pageTitle: 'İstifadə Şərtləri',
      intro: 'Cook AI-yə xoş gəlmisiniz! Tətbiqimizə daxil olmaqla və ya onu istifadə etməklə bu İstifadə Şərtlərinə əməl etməyi qəbul edirsiniz.',
      s1Title: '1. Şərtlərin Qəbulu',
      s1Text: 'Cook AI istifadə etməklə, bu Şərtləri və Məxfilik Siyasətimizi oxuduğunuzu, başa düşdüyünüzü və onlara riayət etməyi qəbul etdiyinizi etiraf edirsiniz.',
      s2Title: '2. Xidmətin Təsviri',
      s2Intro: 'Cook AI, verdiyiniz maddələrə əsasən fərdi reseptlər yaradan süni intellekt tətbiqidir. Xidmətimizə daxildir:',
      s2Items: [
        'Daxil etdiyiniz maddələrə əsasən süni intellekt resept təklifləri',
        'Bişirmə təlimatları və hazırlıq addımları',
        'Qida dəyəri təxminləri',
        'Resept fərdiləşdirmə seçimləri',
        'Maddə əvəzliyi tövsiyələri',
      ],
      warningTitle: '⚠️ Vacib Xəbərdarlıq',
      warningText: 'Cook AI süni intellekt tərəfindən yaradılmış resept təklifləri təqdim edir. Dəqiqliyə çalışsaq da, bütün reseptlərin təhlükəsiz, dəqiq və ya pəhriz ehtiyaclarınıza uyğun olduğunu zəmanət verə bilmərik.',
      s3Title: '3. İstifadəçi Məsuliyyəti',
      s3Intro: 'Cook AI istifadə edərkən razılaşırsınız ki:',
      s3Items: [
        'Dəqiq maddə məlumatları təqdim edəcəksiniz',
        'Xidmətdən yalnız qanuni məqsədlər üçün istifadə edəcəksiniz',
        'Süni intellekt sistemini manipulyasiya etməyə cəhd etməyəcəksiniz',
        'Bişirməzdən əvvəl reseptin təhlükəsizliyini yoxlayacaqsınız',
        'Qida allergiyaları və məhdudiyyətlərini yoxlayacaqsınız',
        'Düzgün qida təhlükəsizliyi təcrübələrindən istifadə edəcəksiniz',
      ],
      s4Title: '4. Süni İntellekt tərəfindən Yaradılmış Məzmun',
      s4Intro: 'Bütün reseptlər süni intellekt texnologiyası tərəfindən yaradılır. Lütfən nəzərə alın:',
      s4Items: [
        'Yaradılmış reseptlər bəzən səhvlər ehtiva edə bilər',
        'Bişirmə vaxtları və temperaturları təhlükəsizlik üçün yoxlanılmalıdır',
        'Qida məlumatları təxminidir və tam dəqiq olmaya bilər',
        'Həmişə maddə uyğunluğunu və qida təhlükəsizliyi qaydalarını yoxlayın',
      ],
      s5Title: '5. Qida Təhlükəsizliyi və Allergiyalar',
      s5Intro: 'Aşağıdakılar üçün yalnız siz məsulsunuz:',
      s5Items: [
        'Bişirməzdən əvvəl bütün maddələrdə allergenlər yoxlamaq',
        'Qidanın təhlükəsiz temperaturlarda bişirilməsini təmin etmək',
        'Düzgün qida emalı və saxlama prosedurlarına əməl etmək',
        'Pəhriz məhdudiyyətləri üçün tibb mütəxəssislərinə müraciət etmək',
        'Maddə təzəliyini və keyfiyyətini yoxlamaq',
      ],
      s5Note: 'Cook AI, xidmətimiz tərəfindən yaradılan reseptlərdən qaynaqlanan allergik reaksiyalardan, qida qaynaqlı xəstəliklərdən və ya sağlamlıq problemlərindən məsul deyil.',
      s6Title: '6. Əqli Mülkiyyət',
      s6Intro: 'Cook AI tətbiqi əqli mülkiyyət qanunları ilə qorunur. Süni intellekt tərəfindən yaradılmış reseptlər şəxsi istifadəniz üçün təqdim edilir. Edə bilərsiniz:',
      s6Items: [
        'Reseptləri şəxsi, qeyri-kommersiya bişirmə üçün istifadə etmək',
        'Reseptləri dostlarınız və ailənizlə paylaşmaq',
        'Reseptləri tərcihləriniize uyğun dəyişdirmək',
      ],
      s6Note: 'İcazəsiz Cook AI tərəfindən yaradılan reseptləri yenidən nəşr etmək, yenidən paylamaq və ya kommersiya məqsədi ilə istifadə etmək olmaz.',
      s7Title: '7. Məsuliyyətin Məhdudlaşdırılması',
      s7Intro: 'Cook AI xidmətini "olduğu kimi" heç bir zəmanət olmadan təqdim edir. Aşağıdakılara görə məsul deyilik:',
      s7Items: [
        'Süni intellekt reseptlərinin dəqiqliyi və etibarlılığı',
        'Qida təhlükəsizliyi problemləri və ya allergik reaksiyalar',
        'Bişirmə uğursuzluqları',
        'Xidmətdən istifadədən yaranan zərərlər',
        'Xidmət kəsilmələri',
      ],
      s8Title: '8. Xidmət Dəyişiklikləri',
      s8Text: 'Cook AI-ni istənilən vaxt xəbərdarlıq olmadan dəyişdirmək, dayandırmaq və ya ləğv etmək hüququnu özümüzdə saxlayırıq.',
      s9Title: '9. Qadağan Edilmiş İstifadə',
      s9Intro: 'Cook AI-dən istifadə edə bilməzsiniz:',
      s9Items: [
        'Zərərli məqsədlər üçün reseptlər yaratmaq',
        'Süni intellekt sistemini ters mühəndislik etməyə cəhd etmək',
        'Xidmətimizden məlumat çıxarmaq',
        'Tətbiq olunan qanun və qaydalara zidd olmaq',
        'Digər istifadəçilərin xidmətə girişinə mane olmaq',
      ],
      s10Title: '10. Xitam',
      s10Text: 'Hər hansı bir səbəbdən, xəbərdarlıq olmadan Cook AI-yə girişinizi istənilən vaxt dayandırmaq hüququnu özümüzdə saxlayırıq.',
      s11Title: '11. Tətbiq Olunan Qanun',
      s11Text: 'Bu Şərtlər tətbiq olunan qanunlarla tənzimlənir. Bu şərtlərdən yaranan mübahisələr müvafiq hüquqi kanallar vasitəsilə həll edilir.',
      s12Title: '12. Abunəliklər və Ödəniş',
      s12Intro: 'Cook AI premium funksiyaları açan avtomatik yenilənən abunəlik planları ("Cook AI Pro") təklif edir. Abunə olmaqla aşağıdakı şərtlərə razılaşırsınız:',
      s12Items: [
        'Abunəlik Planları: Cook AI Pro Həftəlik plan (7 günlük pulsuz sınaq daxil) və ya İllik plan (ən yaxşı dəyər) kimi mövcuddur.',
        'Pulsuz Sınaq: Həftəlik plan 7 günlük pulsuz sınaq dövrü daxildir. Sınaq başa çatmazdan ən az 24 saat əvvəl ləğv edilmədikdə abunəlik avtomatik olaraq başlayır.',
        'Qiymətlər: Abunəlik qiymətləri satınalma zamanı App Store-da göstərilir və ölkəyə görə dəyişə bilər.',
        'Ödəniş: Satınalmanın təsdiqlənməsindən və ya pulsuz sınaq dövrünün bitməsindən sonra Apple ID hesabınızdan ödəniş tutulur.',
        'Avtomatik Yeniləmə: Abunəliklər yeniləmə tarixindən ən az 24 saat əvvəl ləğv edilmədikdə hər dövrün sonunda avtomatik yenilənir. Mövcud dövrün bitməsinə 24 saat qalmış Apple ID hesabınızdan ödəniş tutulur.',
        'Ləğvetmə: Cihaz Parametrləri → [Adınız] → Abunəliklər vasitəsilə istənilən vaxt ləğv edə bilərsiniz. Mövcud dövrün sonuna qədər giriş davam edir.',
        'Geri Ödəmə Yoxdur: Abunəlik ödənişləri tətbiq olunan qanuna və ya Apple-ın geri ödəmə siyasətinə uyğun hallar istisna olmaqla geri qaytarılmır.',
        'Premium Xüsusiyyətlər: Cook AI Pro limitsiz AI reseptlərini, reseptləri saxlama və təşkil etməni, qabaqcıl qida məlumatlarını və reklamsız təcrübəni açır.',
      ],
      s12Note: 'Abunəlik idarəçiliyi Apple ID hesab parametrləri vasitəsilə həyata keçirilir. Ödəniş sualları üçün Apple Dəstəyi ilə əlaqə saxlayın.',
      contactTitle: 'Bizimlə Əlaqə',
      contactText: 'Bu İstifadə Şərtləri haqqında suallarınız varsa, bizimlə əlaqə saxlayın:',
      contactText2: 'Cook AI istifadə etməklə, bu Şərtləri oxuduğunuzu, başa düşdüyünüzü və onlara riayət etməyi qəbul etdiyinizi etiraf edirsiniz. Xoş bişirmələr! 👨‍🍳',
    },
  },

  // ──────────────────────────────── FRENCH ───────────────────────────────
  fr: {
    backButton: '← Retour à Cook AI',
    lastUpdated: 'Dernière mise à jour : Janvier 2025',
    privacy: {
      pageTitle: 'Politique de Confidentialité',
      intro: "Chez Cook AI, nous respectons votre vie privée et nous engageons à protéger vos informations personnelles. Cette Politique de Confidentialité explique comment nous collectons, utilisons et protégeons vos données lorsque vous utilisez notre application de génération de recettes par IA.",
      s1Title: '1. Informations Que Nous Collectons',
      s1Intro: 'Lorsque vous utilisez Cook AI, nous pouvons collecter les types d\'informations suivants :',
      s1Items: [
        "Données d'ingrédients : Informations sur les ingrédients que vous saisissez pour générer des recettes",
        'Préférences de recettes : Vos préférences alimentaires, types de cuisine et méthodes de cuisson',
        "Données d'utilisation : Comment vous interagissez avec notre application, y compris les fonctionnalités utilisées",
        "Informations sur l'appareil : Type de navigateur, dispositif et système d'exploitation pour l'optimisation",
      ],
      s2Title: '2. Comment Nous Utilisons Vos Informations',
      s2Intro: 'Nous utilisons les informations collectées pour :',
      s2Items: [
        'Générer des recommandations de recettes personnalisées basées sur vos ingrédients',
        'Améliorer nos algorithmes IA et la qualité de génération de recettes',
        "Améliorer l'expérience utilisateur et les fonctionnalités de l'application",
        'Analyser les modèles d\'utilisation pour développer de nouvelles fonctionnalités',
        'Assurer la sécurité et le bon fonctionnement de notre service',
      ],
      s3Title: '3. Stockage et Sécurité des Données',
      s3Text: "Vos données sont stockées en toute sécurité et nous mettons en œuvre des mesures de sécurité standard de l'industrie pour les protéger. Nous ne vendons, n'échangeons ni ne louons vos informations personnelles à des tiers.",
      s4Title: '4. IA et Services Tiers',
      s4Text: "Cook AI utilise la technologie d'intelligence artificielle pour générer des recettes. Les données d'ingrédients que vous fournissez peuvent être traitées par nos fournisseurs de services IA pour créer des suggestions de recettes.",
      s5Title: '5. Cookies et Stockage Local',
      s5Text: 'Nous utilisons des technologies de stockage de navigateur pour sauvegarder vos préférences et améliorer votre expérience. Vous pouvez effacer ces données à tout moment via les paramètres de votre navigateur.',
      s6Title: '6. Vos Droits',
      s6Intro: 'Vous avez le droit de :',
      s6Items: [
        'Accéder aux données personnelles que nous détenons à votre sujet',
        'Demander la correction de données inexactes',
        'Demander la suppression de vos données',
        'Vous opposer à la collecte de données (bien que cela puisse limiter les fonctionnalités)',
        'Exporter vos données dans un format portable',
      ],
      s7Title: '7. Confidentialité des Enfants',
      s7Text: "Cook AI est conçu pour le grand public. Nous ne collectons pas sciemment d'informations personnelles auprès d'enfants de moins de 13 ans.",
      s8Title: '8. Modifications de Cette Politique',
      s8Text: 'Nous pouvons mettre à jour cette Politique de Confidentialité de temps en temps. Nous vous informerons de tout changement en publiant la nouvelle politique sur cette page.',
      contactTitle: 'Nous Contacter',
      contactText: 'Si vous avez des questions sur cette Politique de Confidentialité, veuillez nous contacter à :',
    },
    terms: {
      pageTitle: "Conditions d'Utilisation",
      intro: "Bienvenue sur Cook AI ! En accédant à notre application ou en l'utilisant, vous acceptez d'être lié par ces Conditions d'Utilisation.",
      s1Title: '1. Acceptation des Conditions',
      s1Text: "En utilisant Cook AI, vous reconnaissez avoir lu, compris et accepté d'être lié par ces Conditions d'Utilisation et notre Politique de Confidentialité.",
      s2Title: '2. Description du Service',
      s2Intro: 'Cook AI est une application alimentée par l\'IA qui génère des recettes personnalisées basées sur vos ingrédients. Notre service comprend :',
      s2Items: [
        "Suggestions de recettes générées par IA selon vos ingrédients",
        'Instructions de cuisson et étapes de préparation',
        'Estimations des informations nutritionnelles',
        'Options de personnalisation des recettes',
        "Recommandations de substitutions d'ingrédients",
      ],
      warningTitle: '⚠️ Avertissement Important',
      warningText: "Cook AI fournit des suggestions de recettes générées par intelligence artificielle. Bien que nous visions l'exactitude, nous ne pouvons pas garantir que toutes les recettes sont sûres, précises ou adaptées à vos besoins alimentaires.",
      s3Title: "3. Responsabilités de l'Utilisateur",
      s3Intro: 'En utilisant Cook AI, vous acceptez de :',
      s3Items: [
        'Fournir des informations précises sur les ingrédients',
        'Utiliser le service uniquement à des fins légales',
        "Ne pas tenter de manipuler ou d'abuser du système IA",
        'Vérifier la sécurité et la pertinence des recettes avant de cuisiner',
        'Vérifier les allergies alimentaires et les restrictions',
        'Respecter les bonnes pratiques de sécurité alimentaire',
      ],
      s4Title: '4. Contenu Généré par IA',
      s4Intro: 'Toutes les recettes et instructions de cuisson sont générées par intelligence artificielle. Veuillez noter :',
      s4Items: [
        'Les recettes générées peuvent parfois contenir des erreurs',
        'Les temps et températures de cuisson doivent être vérifiés pour la sécurité',
        'Les informations nutritionnelles sont estimées et peuvent ne pas être entièrement exactes',
        "Vérifiez toujours la compatibilité des ingrédients et les directives de sécurité alimentaire",
      ],
      s5Title: '5. Sécurité Alimentaire et Allergies',
      s5Intro: 'Vous êtes seul responsable de :',
      s5Items: [
        'Vérifier tous les ingrédients pour les allergènes avant la cuisson',
        'S\'assurer que les aliments sont cuits à des températures sûres',
        'Suivre les procédures appropriées de manipulation et de stockage',
        'Consulter des professionnels de santé concernant les restrictions alimentaires',
        'Vérifier la fraîcheur et la qualité des ingrédients',
      ],
      s5Note: "Cook AI n'est pas responsable des réactions allergiques, maladies d'origine alimentaire ou problèmes de santé résultant de recettes générées par notre service.",
      s6Title: '6. Propriété Intellectuelle',
      s6Intro: "L'application Cook AI est protégée par les lois sur la propriété intellectuelle. Les recettes générées par IA sont fournies pour votre usage personnel. Vous pouvez :",
      s6Items: [
        'Utiliser les recettes pour une cuisine personnelle et non commerciale',
        'Partager des recettes avec vos amis et votre famille',
        'Modifier les recettes selon vos préférences',
      ],
      s6Note: 'Vous ne pouvez pas republier, redistribuer ou exploiter commercialement des recettes générées par Cook AI sans autorisation.',
      s7Title: '7. Limitation de Responsabilité',
      s7Intro: 'Cook AI fournit le service « tel quel » sans garanties. Nous ne sommes pas responsables de :',
      s7Items: [
        "L'exactitude ou la fiabilité des recettes générées par IA",
        'Les problèmes de sécurité alimentaire ou les réactions allergiques',
        'Les échecs culinaires ou résultats insatisfaisants',
        "Les dommages résultant de l'utilisation de notre service",
        'Les interruptions de service ou problèmes techniques',
      ],
      s8Title: '8. Modifications du Service',
      s8Text: 'Nous nous réservons le droit de modifier, suspendre ou interrompre Cook AI à tout moment sans préavis.',
      s9Title: '9. Utilisations Interdites',
      s9Intro: 'Vous ne pouvez pas utiliser Cook AI pour :',
      s9Items: [
        'Générer des recettes à des fins nuisibles ou dangereuses',
        'Tenter de rétroconcevoir ou manipuler notre système IA',
        'Extraire des données de notre service',
        'Violer les lois et réglementations applicables',
        "Perturber l'accès d'autres utilisateurs au service",
      ],
      s10Title: '10. Résiliation',
      s10Text: 'Nous nous réservons le droit de résilier ou de suspendre votre accès à Cook AI à tout moment, sans préavis.',
      s11Title: '11. Droit Applicable',
      s11Text: 'Ces Conditions sont régies par les lois applicables. Tout litige sera résolu par les voies légales appropriées.',
      s12Title: '12. Abonnements et Facturation',
      s12Intro: 'Cook AI propose des plans d\'abonnement à renouvellement automatique ("Cook AI Pro") qui débloquent des fonctionnalités premium. En vous abonnant, vous acceptez les conditions suivantes :',
      s12Items: [
        'Plans d\'abonnement : Cook AI Pro est disponible en plan Hebdomadaire (avec 7 jours d\'essai gratuit) ou en plan Annuel offrant le meilleur rapport qualité-prix.',
        'Essai gratuit : Le plan Hebdomadaire inclut 7 jours d\'essai gratuit. L\'abonnement commence automatiquement à la fin de la période d\'essai sauf annulation au moins 24 heures avant la fin.',
        'Tarifs : Les prix des abonnements sont affichés dans l\'App Store au moment de l\'achat et peuvent varier selon le pays ou la région.',
        'Paiement : Le paiement est débité de votre compte Apple ID lors de la confirmation d\'achat ou à la fin de la période d\'essai.',
        'Renouvellement automatique : Les abonnements se renouvellent automatiquement à la fin de chaque période sauf annulation au moins 24 heures avant la date de renouvellement. Le débit est effectué dans les 24 heures précédant la fin de la période en cours.',
        'Annulation : Annulez à tout moment via Réglages → [Votre nom] → Abonnements. L\'accès reste actif jusqu\'à la fin de la période en cours.',
        'Aucun remboursement : Les frais d\'abonnement ne sont pas remboursables sauf disposition légale applicable ou politique de remboursement d\'Apple.',
        'Fonctionnalités premium : Cook AI Pro débloque des recettes IA illimitées, la sauvegarde et l\'organisation des recettes, des analyses nutritionnelles avancées et une expérience sans publicité.',
      ],
      s12Note: 'La gestion des abonnements s\'effectue via les paramètres de votre compte Apple ID. Pour toute question de facturation, contactez le Support Apple.',
      contactTitle: 'Nous Contacter',
      contactText: "Si vous avez des questions sur ces Conditions d'Utilisation, veuillez nous contacter à :",
      contactText2: "En utilisant Cook AI, vous reconnaissez avoir lu et compris ces Conditions. Bonne cuisine ! 👨‍🍳",
    },
  },

  // ──────────────────────────────── ITALIAN ──────────────────────────────
  it: {
    backButton: '← Torna a Cook AI',
    lastUpdated: 'Ultimo aggiornamento: Gennaio 2025',
    privacy: {
      pageTitle: 'Informativa sulla Privacy',
      intro: "In Cook AI, rispettiamo la tua privacy e ci impegniamo a proteggere le tue informazioni personali. Questa Informativa spiega come raccogliamo, utilizziamo e proteggiamo i tuoi dati quando usi la nostra applicazione di generazione ricette con IA.",
      s1Title: '1. Informazioni che Raccogliamo',
      s1Intro: 'Quando usi Cook AI, possiamo raccogliere i seguenti tipi di informazioni:',
      s1Items: [
        'Dati ingredienti: Informazioni sugli ingredienti che inserisci per generare ricette',
        'Preferenze ricette: Le tue preferenze alimentari, tipi di cucina e metodi di cottura',
        "Dati di utilizzo: Come interagisci con la nostra app, incluse le funzionalità utilizzate",
        "Informazioni dispositivo: Tipo di browser, dispositivo e sistema operativo per l'ottimizzazione",
      ],
      s2Title: '2. Come Utilizziamo le Tue Informazioni',
      s2Intro: 'Utilizziamo le informazioni raccolte per:',
      s2Items: [
        'Generare raccomandazioni di ricette personalizzate basate sui tuoi ingredienti',
        'Migliorare i nostri algoritmi IA e la qualità di generazione ricette',
        "Migliorare l'esperienza utente e le funzionalità dell'app",
        'Analizzare i modelli di utilizzo per sviluppare nuove funzionalità',
        'Garantire la sicurezza e il corretto funzionamento del servizio',
      ],
      s3Title: '3. Archiviazione e Sicurezza dei Dati',
      s3Text: 'I tuoi dati sono archiviati in modo sicuro e implementiamo misure di sicurezza standard del settore per proteggerli. Non vendiamo, commercializziamo o affittiamo le tue informazioni personali a terzi.',
      s4Title: '4. IA e Servizi di Terze Parti',
      s4Text: "Cook AI utilizza la tecnologia dell'intelligenza artificiale per generare ricette. I dati degli ingredienti che fornisci possono essere elaborati dai nostri fornitori di servizi IA per creare suggerimenti di ricette.",
      s5Title: '5. Cookie e Archiviazione Locale',
      s5Text: 'Utilizziamo tecnologie di archiviazione del browser per salvare le tue preferenze e migliorare la tua esperienza. Puoi cancellare questi dati in qualsiasi momento tramite le impostazioni del browser.',
      s6Title: '6. I Tuoi Diritti',
      s6Intro: 'Hai il diritto di:',
      s6Items: [
        'Accedere ai dati personali che deteniamo su di te',
        'Richiedere la correzione di dati inesatti',
        'Richiedere la cancellazione dei tuoi dati',
        'Opporti alla raccolta dei dati (anche se questo potrebbe limitare le funzionalità)',
        'Esportare i tuoi dati in un formato portatile',
      ],
      s7Title: '7. Privacy dei Minori',
      s7Text: 'Cook AI è progettato per il pubblico generale. Non raccogliamo consapevolmente informazioni personali da bambini di età inferiore a 13 anni.',
      s8Title: '8. Modifiche a Questa Informativa',
      s8Text: 'Potremmo aggiornare questa Informativa sulla Privacy di tanto in tanto. Ti informeremo di eventuali modifiche pubblicando la nuova informativa su questa pagina.',
      contactTitle: 'Contattaci',
      contactText: 'Se hai domande su questa Informativa sulla Privacy, contattaci a:',
    },
    terms: {
      pageTitle: 'Termini di Utilizzo',
      intro: "Benvenuto su Cook AI! Accedendo alla nostra applicazione o utilizzandola, accetti di essere vincolato da questi Termini di Utilizzo.",
      s1Title: '1. Accettazione dei Termini',
      s1Text: "Utilizzando Cook AI, riconosci di aver letto, compreso e accettato di essere vincolato da questi Termini di Utilizzo e dalla nostra Informativa sulla Privacy.",
      s2Title: '2. Descrizione del Servizio',
      s2Intro: "Cook AI è un'applicazione basata su IA che genera ricette personalizzate in base agli ingredienti che fornisci. Il nostro servizio include:",
      s2Items: [
        "Suggerimenti di ricette generate dall'IA in base ai tuoi ingredienti",
        'Istruzioni di cottura e fasi di preparazione',
        'Stime delle informazioni nutrizionali',
        'Opzioni di personalizzazione delle ricette',
        'Raccomandazioni di sostituzione degli ingredienti',
      ],
      warningTitle: '⚠️ Avviso Importante',
      warningText: "Cook AI fornisce suggerimenti di ricette generati dall'intelligenza artificiale. Sebbene miriamo all'accuratezza, non possiamo garantire che tutte le ricette siano sicure, accurate o adatte alle tue esigenze alimentari.",
      s3Title: "3. Responsabilità dell'Utente",
      s3Intro: 'Utilizzando Cook AI, accetti di:',
      s3Items: [
        'Fornire informazioni accurate sugli ingredienti',
        'Utilizzare il servizio solo per scopi leciti',
        'Non tentare di manipolare o abusare del sistema IA',
        'Verificare la sicurezza e l\'idoneità delle ricette prima di cucinare',
        'Controllare allergie alimentari e restrizioni',
        'Utilizzare pratiche di sicurezza alimentare appropriate',
      ],
      s4Title: "4. Contenuto Generato dall'IA",
      s4Intro: "Tutte le ricette e le istruzioni di cottura sono generate dalla tecnologia dell'intelligenza artificiale. Si prega di notare:",
      s4Items: [
        'Le ricette generate possono occasionalmente contenere errori',
        'I tempi e le temperature di cottura devono essere verificati per la sicurezza',
        'Le informazioni nutrizionali sono stimate e potrebbero non essere completamente accurate',
        'Verificare sempre la compatibilità degli ingredienti e le linee guida sulla sicurezza alimentare',
      ],
      s5Title: '5. Sicurezza Alimentare e Allergie',
      s5Intro: 'Sei l\'unico responsabile di:',
      s5Items: [
        'Controllare tutti gli ingredienti per allergeni prima di cucinare',
        'Assicurarsi che il cibo venga cotto a temperature sicure',
        'Seguire corrette procedure di manipolazione e conservazione',
        'Consultare professionisti medici riguardo alle restrizioni alimentari',
        'Verificare la freschezza e la qualità degli ingredienti',
      ],
      s5Note: 'Cook AI non è responsabile per reazioni allergiche, malattie di origine alimentare o problemi di salute risultanti dalle ricette generate dal nostro servizio.',
      s6Title: '6. Proprietà Intellettuale',
      s6Intro: "L'applicazione Cook AI è proteggiuta dalle leggi sulla proprietà intellettuale. Le ricette generate dall'IA sono fornite per uso personale. Puoi:",
      s6Items: [
        'Utilizzare le ricette per cucina personale e non commerciale',
        'Condividere ricette con amici e familiari',
        'Modificare le ricette secondo le tue preferenze',
      ],
      s6Note: 'Non puoi ripubblicare, ridistribuire o sfruttare commercialmente ricette generate da Cook AI senza autorizzazione.',
      s7Title: '7. Limitazione di Responsabilità',
      s7Intro: 'Cook AI fornisce il servizio "così com\'è" senza garanzie. Non siamo responsabili per:',
      s7Items: [
        "Accuratezza o affidabilità delle ricette generate dall'IA",
        'Problemi di sicurezza alimentare o reazioni allergiche',
        'Fallimenti culinari o risultati insoddisfacenti',
        "Danni derivanti dall'utilizzo del nostro servizio",
        'Interruzioni del servizio o problemi tecnici',
      ],
      s8Title: '8. Modifiche al Servizio',
      s8Text: 'Ci riserviamo il diritto di modificare, sospendere o interrompere Cook AI in qualsiasi momento senza preavviso.',
      s9Title: '9. Usi Proibiti',
      s9Intro: 'Non puoi utilizzare Cook AI per:',
      s9Items: [
        'Generare ricette per scopi dannosi o pericolosi',
        'Tentare di fare reverse engineering o manipolare il sistema IA',
        'Estrarre dati dal nostro servizio',
        'Violare leggi o regolamenti applicabili',
        "Interferire con l'accesso di altri utenti al servizio",
      ],
      s10Title: '10. Risoluzione',
      s10Text: 'Ci riserviamo il diritto di terminare o sospendere il tuo accesso a Cook AI in qualsiasi momento, senza preavviso.',
      s11Title: '11. Legge Applicabile',
      s11Text: 'Questi Termini di Utilizzo sono disciplinati dalle leggi applicabili. Qualsiasi controversia sarà risolta attraverso canali legali appropriati.',
      s12Title: '12. Abbonamenti e Fatturazione',
      s12Intro: 'Cook AI offre piani di abbonamento a rinnovo automatico ("Cook AI Pro") che sbloccano funzionalità premium. Abbonandoti, accetti i seguenti termini:',
      s12Items: [
        'Piani di abbonamento: Cook AI Pro è disponibile come piano Settimanale (con 7 giorni di prova gratuita) o piano Annuale con il miglior rapporto qualità-prezzo.',
        'Prova gratuita: Il piano Settimanale include 7 giorni di prova gratuita. L\'abbonamento inizia automaticamente alla fine del periodo di prova salvo cancellazione almeno 24 ore prima della scadenza.',
        'Prezzi: I prezzi degli abbonamenti sono visualizzati nell\'App Store al momento dell\'acquisto e possono variare per paese o regione.',
        'Pagamento: Il pagamento viene addebitato sul tuo account Apple ID alla conferma dell\'acquisto o alla fine del periodo di prova.',
        'Rinnovo automatico: Gli abbonamenti si rinnovano automaticamente alla fine di ogni periodo salvo cancellazione almeno 24 ore prima del rinnovo. L\'addebito avviene entro 24 ore dalla fine del periodo corrente.',
        'Cancellazione: Annulla in qualsiasi momento tramite Impostazioni → [Il tuo nome] → Abbonamenti. L\'accesso rimane attivo fino alla fine del periodo corrente.',
        'Nessun rimborso: I pagamenti degli abbonamenti non sono rimborsabili salvo quanto richiesto dalla legge applicabile o dalle politiche di rimborso di Apple.',
        'Funzionalità premium: Cook AI Pro sblocca ricette IA illimitate, il salvataggio e l\'organizzazione delle ricette, approfondimenti nutrizionali avanzati e un\'esperienza senza pubblicità.',
      ],
      s12Note: 'La gestione degli abbonamenti avviene tramite le impostazioni del tuo account Apple ID. Per domande sulla fatturazione, contatta il Supporto Apple.',
      contactTitle: 'Contattaci',
      contactText: 'Se hai domande su questi Termini di Utilizzo, contattaci a:',
      contactText2: 'Utilizzando Cook AI, riconosci di aver letto e compreso questi Termini di Utilizzo. Buona cucina! 👨‍🍳',
    },
  },

  // ─────────────────────────────── PORTUGUESE ────────────────────────────
  pt: {
    backButton: '← Voltar ao Cook AI',
    lastUpdated: 'Última atualização: Janeiro de 2025',
    privacy: {
      pageTitle: 'Política de Privacidade',
      intro: 'Na Cook AI, respeitamos sua privacidade e estamos comprometidos em proteger suas informações pessoais. Esta Política explica como coletamos, usamos e protegemos seus dados ao usar nosso aplicativo de geração de receitas com IA.',
      s1Title: '1. Informações que Coletamos',
      s1Intro: 'Ao usar o Cook AI, podemos coletar os seguintes tipos de informações:',
      s1Items: [
        'Dados de ingredientes: Informações sobre os ingredientes que você insere para gerar receitas',
        'Preferências de receitas: Suas preferências alimentares, tipos de culinária e métodos de cozimento',
        'Dados de uso: Como você interage com nosso aplicativo, incluindo recursos usados e tempo gasto',
        'Informações do dispositivo: Tipo de navegador, dispositivo e sistema operacional para otimização',
      ],
      s2Title: '2. Como Usamos Suas Informações',
      s2Intro: 'Usamos as informações coletadas para:',
      s2Items: [
        'Gerar recomendações de receitas personalizadas com base nos seus ingredientes',
        'Melhorar nossos algoritmos de IA e a qualidade de geração de receitas',
        'Aprimorar a experiência do usuário e as funcionalidades do aplicativo',
        'Analisar padrões de uso para desenvolver novos recursos',
        'Garantir a segurança e o funcionamento adequado do serviço',
      ],
      s3Title: '3. Armazenamento e Segurança dos Dados',
      s3Text: 'Seus dados são armazenados com segurança e implementamos medidas de segurança padrão da indústria para protegê-los. Não vendemos, negociamos nem alugamos suas informações pessoais a terceiros.',
      s4Title: '4. IA e Serviços de Terceiros',
      s4Text: 'O Cook AI usa tecnologia de inteligência artificial para gerar receitas. Os dados de ingredientes que você fornece podem ser processados por nossos provedores de serviços de IA para criar sugestões de receitas.',
      s5Title: '5. Cookies e Armazenamento Local',
      s5Text: 'Usamos tecnologias de armazenamento do navegador para salvar suas preferências e melhorar sua experiência. Você pode limpar esses dados a qualquer momento pelas configurações do navegador.',
      s6Title: '6. Seus Direitos',
      s6Intro: 'Você tem o direito de:',
      s6Items: [
        'Acessar os dados pessoais que mantemos sobre você',
        'Solicitar a correção de dados imprecisos',
        'Solicitar a exclusão dos seus dados',
        'Recusar a coleta de dados (embora isso possa limitar as funcionalidades)',
        'Exportar seus dados em formato portátil',
      ],
      s7Title: '7. Privacidade de Crianças',
      s7Text: 'O Cook AI é projetado para o público em geral. Não coletamos intencionalmente informações pessoais de crianças menores de 13 anos.',
      s8Title: '8. Alterações nesta Política',
      s8Text: 'Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos você sobre quaisquer alterações publicando a nova política nesta página.',
      contactTitle: 'Contate-nos',
      contactText: 'Se você tiver perguntas sobre esta Política de Privacidade, entre em contato:',
    },
    terms: {
      pageTitle: 'Termos de Uso',
      intro: 'Bem-vindo ao Cook AI! Ao acessar ou usar nosso aplicativo, você concorda em cumprir estes Termos de Uso.',
      s1Title: '1. Aceitação dos Termos',
      s1Text: 'Ao usar o Cook AI, você reconhece que leu, entendeu e concorda em cumprir estes Termos de Uso e nossa Política de Privacidade.',
      s2Title: '2. Descrição do Serviço',
      s2Intro: 'O Cook AI é um aplicativo alimentado por IA que gera receitas personalizadas com base nos ingredientes que você fornece. Nosso serviço inclui:',
      s2Items: [
        'Sugestões de receitas geradas por IA com base nos seus ingredientes',
        'Instruções de cozimento e etapas de preparo',
        'Estimativas de informações nutricionais',
        'Opções de personalização de receitas',
        'Recomendações de substituição de ingredientes',
      ],
      warningTitle: '⚠️ Aviso Importante',
      warningText: 'O Cook AI fornece sugestões de receitas geradas por inteligência artificial. Embora busquemos precisão, não podemos garantir que todas as receitas sejam seguras, precisas ou adequadas às suas necessidades alimentares.',
      s3Title: '3. Responsabilidades do Usuário',
      s3Intro: 'Ao usar o Cook AI, você concorda em:',
      s3Items: [
        'Fornecer informações precisas sobre ingredientes',
        'Usar o serviço apenas para fins legais',
        'Não tentar manipular ou abusar do sistema de IA',
        'Verificar a segurança e adequação das receitas antes de cozinhar',
        'Verificar alergias alimentares e restrições',
        'Usar práticas adequadas de segurança alimentar',
      ],
      s4Title: '4. Conteúdo Gerado por IA',
      s4Intro: 'Todas as receitas e instruções de cozimento são geradas por tecnologia de inteligência artificial. Por favor, observe:',
      s4Items: [
        'As receitas geradas podem ocasionalmente conter erros',
        'Tempos e temperaturas de cozimento devem ser verificados por segurança',
        'As informações nutricionais são estimadas e podem não ser completamente precisas',
        'Sempre verifique a compatibilidade dos ingredientes e as diretrizes de segurança alimentar',
      ],
      s5Title: '5. Segurança Alimentar e Alergias',
      s5Intro: 'Você é o único responsável por:',
      s5Items: [
        'Verificar todos os ingredientes para alérgenos antes de cozinhar',
        'Garantir que os alimentos sejam cozidos em temperaturas seguras',
        'Seguir procedimentos adequados de manuseio e armazenamento',
        'Consultar profissionais de saúde sobre restrições alimentares',
        'Verificar a frescura e qualidade dos ingredientes',
      ],
      s5Note: 'O Cook AI não é responsável por reações alérgicas, doenças transmitidas por alimentos ou problemas de saúde resultantes de receitas geradas pelo nosso serviço.',
      s6Title: '6. Propriedade Intelectual',
      s6Intro: 'O aplicativo Cook AI é protegido por leis de propriedade intelectual. As receitas geradas por IA são fornecidas para uso pessoal. Você pode:',
      s6Items: [
        'Usar receitas para cozinha pessoal e não comercial',
        'Compartilhar receitas com amigos e família',
        'Modificar receitas de acordo com suas preferências',
      ],
      s6Note: 'Você não pode republicar, redistribuir ou explorar comercialmente receitas geradas pelo Cook AI sem permissão.',
      s7Title: '7. Limitação de Responsabilidade',
      s7Intro: 'O Cook AI fornece o serviço "como está" sem garantias. Não somos responsáveis por:',
      s7Items: [
        'Precisão ou confiabilidade de receitas geradas por IA',
        'Problemas de segurança alimentar ou reações alérgicas',
        'Falhas culinárias ou resultados insatisfatórios',
        'Danos resultantes do uso do nosso serviço',
        'Interrupções de serviço ou problemas técnicos',
      ],
      s8Title: '8. Modificações no Serviço',
      s8Text: 'Reservamo-nos o direito de modificar, suspender ou descontinuar o Cook AI a qualquer momento sem aviso prévio.',
      s9Title: '9. Usos Proibidos',
      s9Intro: 'Você não pode usar o Cook AI para:',
      s9Items: [
        'Gerar receitas para fins prejudiciais ou perigosos',
        'Tentar fazer engenharia reversa ou manipular nosso sistema de IA',
        'Extrair dados do nosso serviço',
        'Violar leis ou regulamentos aplicáveis',
        'Interferir no acesso de outros usuários ao serviço',
      ],
      s10Title: '10. Rescisão',
      s10Text: 'Reservamo-nos o direito de encerrar ou suspender seu acesso ao Cook AI a qualquer momento, sem aviso.',
      s11Title: '11. Lei Aplicável',
      s11Text: 'Estes Termos de Uso são regidos pelas leis aplicáveis. Quaisquer disputas serão resolvidas por meio de canais legais apropriados.',
      s12Title: '12. Assinaturas e Faturamento',
      s12Intro: 'O Cook AI oferece planos de assinatura com renovação automática ("Cook AI Pro") que desbloqueiam recursos premium. Ao assinar, você concorda com os seguintes termos:',
      s12Items: [
        'Planos de assinatura: O Cook AI Pro está disponível como plano Semanal (com 7 dias de teste gratuito) ou plano Anual com o melhor custo-benefício.',
        'Teste gratuito: O plano Semanal inclui 7 dias de teste gratuito. A assinatura começa automaticamente ao final do período de teste, salvo cancelamento com pelo menos 24 horas de antecedência.',
        'Preços: Os preços das assinaturas são exibidos na App Store no momento da compra e podem variar por país ou região.',
        'Pagamento: O pagamento é debitado da sua conta Apple ID na confirmação da compra ou ao final do período de teste gratuito.',
        'Renovação automática: As assinaturas se renovam automaticamente ao final de cada período, salvo cancelamento com pelo menos 24 horas de antecedência. O débito ocorre dentro de 24 horas antes do término do período atual.',
        'Cancelamento: Cancele a qualquer momento em Ajustes → [Seu nome] → Assinaturas. O acesso continua até o final do período atual.',
        'Sem reembolso: As taxas de assinatura não são reembolsáveis, exceto conforme exigido pela lei aplicável ou pelas políticas de reembolso da Apple.',
        'Recursos premium: O Cook AI Pro desbloqueia receitas de IA ilimitadas, salvar e organizar receitas, insights nutricionais avançados e experiência sem anúncios.',
      ],
      s12Note: 'O gerenciamento de assinaturas é feito pelas configurações da sua conta Apple ID. Para dúvidas sobre faturamento, entre em contato com o Suporte Apple.',
      contactTitle: 'Contate-nos',
      contactText: 'Se você tiver perguntas sobre estes Termos de Uso, entre em contato:',
      contactText2: 'Ao usar o Cook AI, você reconhece que leu e entendeu estes Termos de Uso. Bom cozimento! 👨‍🍳',
    },
  },

  // ──────────────────────────────── SPANISH ──────────────────────────────
  es: {
    backButton: '← Volver a Cook AI',
    lastUpdated: 'Última actualización: Enero 2025',
    privacy: {
      pageTitle: 'Política de Privacidad',
      intro: 'En Cook AI, respetamos tu privacidad y nos comprometemos a proteger tu información personal. Esta Política explica cómo recopilamos, usamos y protegemos tus datos cuando usas nuestra aplicación de generación de recetas con IA.',
      s1Title: '1. Información que Recopilamos',
      s1Intro: 'Cuando usas Cook AI, podemos recopilar los siguientes tipos de información:',
      s1Items: [
        'Datos de ingredientes: Información sobre los ingredientes que introduces para generar recetas',
        'Preferencias de recetas: Tus preferencias alimentarias, tipos de cocina y métodos de cocción',
        'Datos de uso: Cómo interactúas con nuestra aplicación, incluyendo funciones usadas y tiempo dedicado',
        'Información del dispositivo: Tipo de navegador, dispositivo y sistema operativo para la optimización',
      ],
      s2Title: '2. Cómo Usamos Tu Información',
      s2Intro: 'Usamos la información recopilada para:',
      s2Items: [
        'Generar recomendaciones de recetas personalizadas basadas en tus ingredientes',
        'Mejorar nuestros algoritmos de IA y la calidad de generación de recetas',
        'Mejorar la experiencia del usuario y las funcionalidades de la aplicación',
        'Analizar patrones de uso para desarrollar nuevas funciones',
        'Garantizar la seguridad y el correcto funcionamiento del servicio',
      ],
      s3Title: '3. Almacenamiento y Seguridad de Datos',
      s3Text: 'Tus datos se almacenan de forma segura y aplicamos medidas de seguridad estándar de la industria para protegerlos. No vendemos, intercambiamos ni alquilamos tu información personal a terceros.',
      s4Title: '4. IA y Servicios de Terceros',
      s4Text: 'Cook AI utiliza tecnología de inteligencia artificial para generar recetas. Los datos de ingredientes que proporcionas pueden ser procesados por nuestros proveedores de servicios de IA para crear sugerencias de recetas.',
      s5Title: '5. Cookies y Almacenamiento Local',
      s5Text: 'Usamos tecnologías de almacenamiento del navegador para guardar tus preferencias y mejorar tu experiencia. Puedes borrar estos datos en cualquier momento a través de la configuración del navegador.',
      s6Title: '6. Tus Derechos',
      s6Intro: 'Tienes el derecho a:',
      s6Items: [
        'Acceder a los datos personales que tenemos sobre ti',
        'Solicitar la corrección de datos inexactos',
        'Solicitar la eliminación de tus datos',
        'Rechazar la recopilación de datos (aunque esto puede limitar las funcionalidades)',
        'Exportar tus datos en un formato portable',
      ],
      s7Title: '7. Privacidad de los Menores',
      s7Text: 'Cook AI está diseñado para el público en general. No recopilamos conscientemente información personal de niños menores de 13 años.',
      s8Title: '8. Cambios en Esta Política',
      s8Text: 'Podemos actualizar esta Política de Privacidad de vez en cuando. Te notificaremos cualquier cambio publicando la nueva política en esta página.',
      contactTitle: 'Contáctanos',
      contactText: 'Si tienes preguntas sobre esta Política de Privacidad, contáctanos en:',
    },
    terms: {
      pageTitle: 'Términos de Uso',
      intro: '¡Bienvenido a Cook AI! Al acceder a nuestra aplicación o usarla, aceptas estar sujeto a estos Términos de Uso.',
      s1Title: '1. Aceptación de los Términos',
      s1Text: 'Al usar Cook AI, reconoces que has leído, comprendido y aceptado estar sujeto a estos Términos de Uso y nuestra Política de Privacidad.',
      s2Title: '2. Descripción del Servicio',
      s2Intro: 'Cook AI es una aplicación impulsada por IA que genera recetas personalizadas basadas en los ingredientes que proporcionas. Nuestro servicio incluye:',
      s2Items: [
        'Sugerencias de recetas generadas por IA según tus ingredientes',
        'Instrucciones de cocción y pasos de preparación',
        'Estimaciones de información nutricional',
        'Opciones de personalización de recetas',
        'Recomendaciones de sustitución de ingredientes',
      ],
      warningTitle: '⚠️ Aviso Importante',
      warningText: 'Cook AI proporciona sugerencias de recetas generadas por inteligencia artificial. Aunque buscamos la precisión, no podemos garantizar que todas las recetas sean seguras, precisas o adecuadas para tus necesidades alimentarias.',
      s3Title: '3. Responsabilidades del Usuario',
      s3Intro: 'Al usar Cook AI, aceptas:',
      s3Items: [
        'Proporcionar información precisa sobre ingredientes',
        'Usar el servicio solo para fines legales',
        'No intentar manipular o abusar del sistema de IA',
        'Verificar la seguridad y adecuación de las recetas antes de cocinar',
        'Comprobar alergias alimentarias y restricciones',
        'Usar prácticas adecuadas de seguridad alimentaria',
      ],
      s4Title: '4. Contenido Generado por IA',
      s4Intro: 'Todas las recetas e instrucciones de cocción son generadas por tecnología de inteligencia artificial. Ten en cuenta que:',
      s4Items: [
        'Las recetas generadas pueden ocasionalmente contener errores',
        'Los tiempos y temperaturas de cocción deben verificarse por seguridad',
        'La información nutricional es estimada y puede no ser completamente precisa',
        'Verifica siempre la compatibilidad de los ingredientes y las pautas de seguridad alimentaria',
      ],
      s5Title: '5. Seguridad Alimentaria y Alergias',
      s5Intro: 'Eres el único responsable de:',
      s5Items: [
        'Verificar todos los ingredientes en busca de alérgenos antes de cocinar',
        'Asegurarte de que los alimentos se cocinen a temperaturas seguras',
        'Seguir procedimientos adecuados de manejo y almacenamiento',
        'Consultar a profesionales médicos sobre restricciones alimentarias',
        'Verificar la frescura y calidad de los ingredientes',
      ],
      s5Note: 'Cook AI no es responsable de reacciones alérgicas, enfermedades transmitidas por alimentos o problemas de salud resultantes de recetas generadas por nuestro servicio.',
      s6Title: '6. Propiedad Intelectual',
      s6Intro: 'La aplicación Cook AI está protegida por las leyes de propiedad intelectual. Las recetas generadas por IA se proporcionan para uso personal. Puedes:',
      s6Items: [
        'Usar recetas para cocina personal y no comercial',
        'Compartir recetas con amigos y familia',
        'Modificar recetas según tus preferencias',
      ],
      s6Note: 'No puedes republicar, redistribuir ni explotar comercialmente recetas generadas por Cook AI sin permiso.',
      s7Title: '7. Limitación de Responsabilidad',
      s7Intro: 'Cook AI proporciona el servicio "tal cual" sin garantías. No somos responsables de:',
      s7Items: [
        'Precisión o fiabilidad de recetas generadas por IA',
        'Problemas de seguridad alimentaria o reacciones alérgicas',
        'Fallos culinarios o resultados insatisfactorios',
        'Daños derivados del uso de nuestro servicio',
        'Interrupciones del servicio o problemas técnicos',
      ],
      s8Title: '8. Modificaciones del Servicio',
      s8Text: 'Nos reservamos el derecho de modificar, suspender o discontinuar Cook AI en cualquier momento sin previo aviso.',
      s9Title: '9. Usos Prohibidos',
      s9Intro: 'No puedes usar Cook AI para:',
      s9Items: [
        'Generar recetas para fines dañinos o peligrosos',
        'Intentar realizar ingeniería inversa o manipular nuestro sistema de IA',
        'Extraer datos de nuestro servicio',
        'Violar leyes o regulaciones aplicables',
        'Interferir con el acceso de otros usuarios al servicio',
      ],
      s10Title: '10. Terminación',
      s10Text: 'Nos reservamos el derecho de terminar o suspender tu acceso a Cook AI en cualquier momento, sin previo aviso.',
      s11Title: '11. Ley Aplicable',
      s11Text: 'Estos Términos de Uso se rigen por las leyes aplicables. Cualquier disputa se resolverá a través de canales legales apropiados.',
      s12Title: '12. Suscripciones y Facturación',
      s12Intro: 'Cook AI ofrece planes de suscripción con renovación automática ("Cook AI Pro") que desbloquean funciones premium. Al suscribirte, aceptas los siguientes términos:',
      s12Items: [
        'Planes de suscripción: Cook AI Pro está disponible como plan Semanal (con 7 días de prueba gratuita) o plan Anual con el mejor valor.',
        'Prueba gratuita: El plan Semanal incluye 7 días de prueba gratuita. La suscripción comienza automáticamente al final del período de prueba salvo cancelación al menos 24 horas antes.',
        'Precios: Los precios de las suscripciones se muestran en el App Store en el momento de la compra y pueden variar según el país o región.',
        'Pago: El pago se carga a tu cuenta Apple ID al confirmar la compra o al finalizar el período de prueba gratuito.',
        'Renovación automática: Las suscripciones se renuevan automáticamente al final de cada período salvo cancelación al menos 24 horas antes de la fecha de renovación. El cargo se realiza dentro de las 24 horas previas al fin del período actual.',
        'Cancelación: Cancela en cualquier momento desde Ajustes → [Tu nombre] → Suscripciones. El acceso se mantiene hasta el final del período actual.',
        'Sin reembolsos: Las tarifas de suscripción no son reembolsables salvo lo requerido por la ley aplicable o las políticas de reembolso de Apple.',
        'Funciones premium: Cook AI Pro desbloquea recetas de IA ilimitadas, guardar y organizar recetas, información nutricional avanzada y una experiencia sin publicidad.',
      ],
      s12Note: 'La gestión de suscripciones se realiza desde la configuración de tu cuenta Apple ID. Para consultas sobre facturación, contacta con el Soporte de Apple.',
      contactTitle: 'Contáctanos',
      contactText: 'Si tienes preguntas sobre estos Términos de Uso, contáctanos en:',
      contactText2: 'Al usar Cook AI, reconoces que has leído y comprendido estos Términos de Uso. ¡Buen provecho! 👨‍🍳',
    },
  },
};
